import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import {
  fetchSpaces, fetchMySpace, fetchFolders, fetchFolderById, fetchFolderPath, fetchItems,
  fetchRecentItems, fetchStarredItems, fetchTrashedItems, fetchSharedItems,
  createFolder, trashFolder, trashItem, restoreItem, purgeItem,
  starItem, duplicateItem, moveItem, updateItem, createFileRequest,
  createLink, downloadFile,
} from '../services/api';
import { loadRecentCache, markOpenedLocally } from '../services/recentCache';
import { useAppStore } from '../store/useAppStore';
import type { DriveItem, Folder, Space, BreadcrumbItem } from '../types';
import Icon from '../components/Icon';
import Button from '../components/Button';
import Chip from '../components/Chip';
import Modal from '../components/Modal';
import Drawer from '../components/Drawer';
import Avatar from '../components/Avatar';
import ContextMenu, { ContextMenuItem } from '../components/ContextMenu';
import { Thumbnail } from '../components/TypeGlyph';
import DetailsPanel from './DetailsPanel';
import Viewer from './Viewer';
import { useUpload } from '../context/UploadContext';

function formatBytes(bytes: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function relativeTime(dateStr?: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

type SortKey = 'modified' | 'name' | 'size' | 'last_used';
type ViewModeLocal = 'grid' | 'list';

const TYPE_FILTERS: { key: string; label: string; icon: string; exts: string[] }[] = [
  { key: 'pdf', label: 'PDF document', icon: 'file', exts: ['pdf'] },
  { key: 'doc', label: 'Word document', icon: 'file', exts: ['doc', 'docx'] },
  { key: 'sheet', label: 'Spreadsheet', icon: 'file', exts: ['xls', 'xlsx'] },
  { key: 'slides', label: 'Presentation', icon: 'file', exts: ['ppt', 'pptx'] },
  { key: 'image', label: 'Image', icon: 'image', exts: ['jpg', 'jpeg', 'png', 'gif'] },
  { key: 'video', label: 'Video', icon: 'video', exts: ['mp4', 'mov'] },
  { key: 'audio', label: 'Audio', icon: 'music', exts: ['mp3', 'wav'] },
  { key: 'text', label: 'Text file', icon: 'file', exts: ['txt', 'md'] },
  { key: 'archive', label: 'Archive', icon: 'layers', exts: ['zip', 'rar'] },
  { key: 'design', label: 'Design file', icon: 'sparkle', exts: ['fig', 'sketch'] },
];

const TAG_FILTERS = ['Any tag', 'Important', 'Review', 'Final', 'Draft', 'Confidential'];
const SIZE_FILTERS = ['Any size', 'Larger than 1MB', 'Larger than 10MB', 'Larger than 100MB'];

function itemExt(item: DriveItem): string {
  const lastDot = item.name.lastIndexOf('.');
  return lastDot > 0 ? item.name.slice(lastDot + 1).toLowerCase() : '';
}

interface Props {
  spaceId?: string;
  folderId?: string;
  mode?: 'browse' | 'recent' | 'starred' | 'trash' | 'shared';
  /** Item to select on arrival — set when navigating from a search hit. */
  highlightItemId?: string;
  onNavigate?: (spaceId: string, folderId?: string) => void;
}

export default function Browse({ spaceId, folderId, mode = 'browse', highlightItemId, onNavigate }: Props) {
  const { toast, isAdmin } = useAppStore();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<ViewModeLocal>('grid');
  const [sort, setSort] = useState<SortKey>('modified');
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detailItem, setDetailItem] = useState<DriveItem | null>(null);
  const [viewerItem, setViewerItem] = useState<DriveItem | null>(null);
  const { openUpload } = useUpload();
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; item: DriveItem | null; folder: Folder | null } | null>(null);
  const [filterExts, setFilterExts] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilters, setTypeFilters] = useState<Set<string>>(new Set());
  const [draftTypeFilters, setDraftTypeFilters] = useState<Set<string>>(new Set());
  const [ownerFilter, setOwnerFilter] = useState('Anyone');
  const [draftOwnerFilter, setDraftOwnerFilter] = useState('Anyone');
  const [tagFilter, setTagFilter] = useState('Any tag');
  const [draftTagFilter, setDraftTagFilter] = useState('Any tag');
  const [sizeFilter, setSizeFilter] = useState('Any size');
  const [draftSizeFilter, setDraftSizeFilter] = useState('Any size');
  const [sharedOnly, setSharedOnly] = useState(false);
  const [draftSharedOnly, setDraftSharedOnly] = useState(false);
  const [renameTarget, setRenameTarget] = useState<DriveItem | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renameExt, setRenameExt] = useState('');
  const [detailTab, setDetailTab] = useState('details');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTarget, setMoveTarget] = useState<DriveItem | null>(null);
  const lastClickedRef = useRef<string | null>(null);

  const { data: spaces = [] } = useQuery({ queryKey: qk.spaces(), queryFn: fetchSpaces, enabled: mode === 'browse' });
  const { data: currentFolder } = useQuery({
    queryKey: ['folder', folderId],
    queryFn: () => fetchFolderById(folderId!),
    enabled: !!folderId,
  });
  // Ancestor chain for the breadcrumb. The API returns the space first; the
  // title row renders that label itself, so drop it here.
  const { data: folderPath = [] } = useQuery({
    queryKey: ['folder', folderId, 'path'],
    queryFn: () => fetchFolderPath(folderId!),
    enabled: !!folderId,
  });
  const folderCrumbs = folderPath.filter(p => p.type === 'folder');
  const { data: folders = [], isLoading: foldersLoading } = useQuery({
    queryKey: qk.folders(spaceId ?? '', folderId),
    queryFn: () => fetchFolders(spaceId!, folderId),
    enabled: mode === 'browse' && !!spaceId,
  });
  const { data: items = [], isLoading: itemsLoading } = useQuery({
    queryKey: mode === 'browse' ? qk.items(spaceId ?? '', folderId)
      : mode === 'recent' ? qk.recent()
      : mode === 'starred' ? qk.starred()
      : mode === 'shared' ? qk.shared()
      : qk.trash(),
    queryFn: () => {
      if (mode === 'recent') return fetchRecentItems();
      if (mode === 'starred') return fetchStarredItems();
      if (mode === 'shared') return fetchSharedItems();
      if (mode === 'trash') return fetchTrashedItems();
      return fetchItems(spaceId!, folderId);
    },
    enabled: mode !== 'browse' || !!spaceId,
    // Recent renders instantly from the last known list (localStorage) while
    // the real fetch reconciles in the background — no spinner on revisit.
    placeholderData: mode === 'recent' ? loadRecentCache : undefined,
  });

  const trashMutation = useMutation({
    mutationFn: (id: string) => trashItem(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['items'] }); toast('Moved to trash'); },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const starMutation = useMutation({
    mutationFn: ({ id, starred }: { id: string; starred: boolean }) => starItem(id, starred),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['items'] }),
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const createFolderMutation = useMutation({
    mutationFn: (name: string) => createFolder(spaceId!, name, folderId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.folders(spaceId!, folderId) });
      qc.invalidateQueries({ queryKey: qk.items(spaceId!, folderId) });
      setShowNewFolder(false);
      setNewFolderName('');
      toast('Folder created');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const trashFolderMutation = useMutation({
    mutationFn: (id: string) => trashFolder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.folders(spaceId!, folderId) });
      toast('Folder moved to trash');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => restoreItem(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.trash() }); toast('Restored'); },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const purgeMutation = useMutation({
    mutationFn: (id: string) => purgeItem(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: qk.trash() }); toast('Deleted permanently'); },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => updateItem(id, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      setRenameTarget(null);
      toast('Renamed');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      moveItem(id, spaceId!, folderId ?? undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['items'] });
      setShowMoveModal(false);
      setMoveTarget(null);
      toast('Moved successfully');
    },
    onError: (e: Error) => toast(e.message, 'danger'),
  });

  const sizeThreshold = sizeFilter === 'Larger than 1MB' ? 1024 * 1024
    : sizeFilter === 'Larger than 10MB' ? 10 * 1024 * 1024
    : sizeFilter === 'Larger than 100MB' ? 100 * 1024 * 1024
    : 0;
  const activeFilterCount = typeFilters.size + (tagFilter !== 'Any tag' ? 1 : 0) + (sizeFilter !== 'Any size' ? 1 : 0) + (sharedOnly ? 1 : 0);

  // Sort + filter items
  const displayItems = [...items]
    .filter(item => {
      if (mode === 'starred' && !item.starred) return false;
      if (typeFilters.size > 0) {
        const ext = itemExt(item);
        const matches = TYPE_FILTERS.some(t => typeFilters.has(t.key) && t.exts.includes(ext));
        if (!matches) return false;
      }
      if (tagFilter !== 'Any tag' && !(item.tags ?? []).some(t => t.name === tagFilter)) return false;
      if (sizeThreshold > 0 && Number(item.currentVersion?.size_bytes ?? item.size_bytes ?? 0) < sizeThreshold) return false;
      if (sharedOnly && !item.link) return false;
      return true;
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sort === 'name') cmp = a.name.localeCompare(b.name);
      else if (sort === 'size') cmp = Number(a.currentVersion?.size_bytes ?? a.size_bytes ?? 0) - Number(b.currentVersion?.size_bytes ?? b.size_bytes ?? 0);
      else cmp = new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      return sortAsc ? -cmp : cmp;
    });

  const handleSelect = useCallback((id: string, e: React.MouseEvent) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (e.metaKey || e.ctrlKey) {
        if (next.has(id)) next.delete(id); else next.add(id);
      } else if (e.shiftKey && lastClickedRef.current) {
        const ids = displayItems.map(i => i.id);
        const from = ids.indexOf(lastClickedRef.current);
        const to = ids.indexOf(id);
        const range = ids.slice(Math.min(from, to), Math.max(from, to) + 1);
        range.forEach(i => next.add(i));
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
    lastClickedRef.current = id;
  }, [displayItems]);

  function handleItemClick(item: DriveItem, e: React.MouseEvent) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      handleSelect(item.id, e);
    } else {
      setDetailItem(item);
    }
  }

  function handleCheckboxClick(item: DriveItem, e: React.MouseEvent) {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
      return next;
    });
    lastClickedRef.current = item.id;
  }

  function openViewer(item: DriveItem) {
    setViewerItem(item);
    markOpenedLocally(item);
  }

  function handleItemDblClick(item: DriveItem) {
    openViewer(item);
  }

  function handleRightClick(e: React.MouseEvent, item?: DriveItem, folder?: Folder) {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, item: item ?? null, folder: folder ?? null });
  }

  function buildItemContextMenu(item: DriveItem): ContextMenuItem[] {
    const isHeld = item.hold_active;
    const isTrashed = item.state === 'trashed';
    if (isTrashed) {
      return [
        { label: 'Restore', icon: 'check', onClick: () => restoreMutation.mutate(item.id) },
        { divider: true },
        { label: 'Delete permanently', icon: 'trash', danger: true, onClick: () => purgeMutation.mutate(item.id) },
      ];
    }
    return [
      { label: 'Open', icon: 'eye', onClick: () => openViewer(item) },
      { label: 'Download', icon: 'download', onClick: () => downloadFile(item.id, item.name).catch((e: Error) => toast(e.message, 'danger')) },
      { divider: true },
      { label: 'Share', icon: 'share', onClick: () => { setDetailItem(item); setDetailTab('sharing'); } },
      { label: 'Copy link', icon: 'link', onClick: () => {
          const doLink = item.link?.token
            ? Promise.resolve(item.link.token)
            : createLink(item.id).then((l: any) => {
                qc.invalidateQueries({ queryKey: ['items'] });
                return l.token ?? l.url_token;
              });
          doLink.then((token: string) => {
            if (!token) { toast('Could not get link token', 'danger'); return; }
            navigator.clipboard.writeText(`${window.location.origin}/s/${token}`);
            toast('Link copied to clipboard');
          }).catch((e: Error) => toast(e.message, 'danger'));
        }
      },
      { label: 'Send transfer', icon: 'send', onClick: () => navigate('/transfers') },
      { divider: true },
      { label: item.starred ? 'Remove star' : 'Add star', icon: 'star', onClick: () => starMutation.mutate({ id: item.id, starred: !item.starred }) },
      { label: 'Rename', icon: 'edit', disabled: isHeld, onClick: () => {
          const lastDot = item.name.lastIndexOf('.');
          const ext = lastDot > 0 ? item.name.slice(lastDot) : '';
          setRenameTarget(item);
          setRenameName(lastDot > 0 ? item.name.slice(0, lastDot) : item.name);
          setRenameExt(ext);
        } },
      { label: 'Make a copy', icon: 'copy', onClick: () => duplicateItem(item.id).then(() => qc.invalidateQueries({ queryKey: ['items'] })) },
      { label: 'Move to…', icon: 'move', disabled: isHeld, onClick: () => { setMoveTarget(item); setShowMoveModal(true); } },
      { divider: true },
      { label: 'Move to Trash', icon: 'trash', danger: true, disabled: isHeld,
        onClick: () => trashMutation.mutate(item.id) },
    ];
  }

  // Keep the details panel in sync with fresh query data after mutations
  const liveDetailItem = detailItem ? (displayItems.find(i => i.id === detailItem.id) ?? detailItem) : null;

  const selArr = [...selected];
  const selItems = displayItems.filter(i => selected.has(i.id));
  const selBytes = selItems.reduce((s, i) => s + Number(i.currentVersion?.size_bytes ?? i.size_bytes ?? 0), 0);
  const heldSel = selItems.filter(i => i.hold_active).length;
  const allSelStarred = selItems.length > 0 && selItems.every(i => i.starred);

  const isLoading = itemsLoading || foldersLoading;

  // Arriving from a search hit: select it once its folder's items have loaded so
  // the user can see which file the result meant.
  useEffect(() => {
    if (!highlightItemId || !items.some(i => i.id === highlightItemId)) return;
    setSelected(new Set([highlightItemId]));
  }, [highlightItemId, items]);

  return (
    <div style={{ display: 'flex', flex: 1, minWidth: 0, height: '100%' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {/* Title row */}
        <div className="sk-page-head" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 18px 0', flexShrink: 0, flexWrap: 'wrap' }}>
          <div className="sk-page-title" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontWeight: 700, fontSize: 'var(--fs-2xl)', color: 'var(--ink)', flexWrap: 'wrap' }}>
            <span style={{ cursor: folderId ? 'pointer' : 'default' }} onClick={() => onNavigate?.(spaceId ?? '')}>
              {mode === 'recent' ? 'Recent' : mode === 'starred' ? 'Starred' : mode === 'shared' ? 'Shared with me' : mode === 'trash' ? 'Trash' : 'My Drive'}
            </span>
            {/* Full ancestor chain — every crumb but the last navigates. While the
                path loads, fall back to the current folder alone rather than
                collapsing to nothing. */}
            {folderId && (folderCrumbs.length ? folderCrumbs : currentFolder ? [currentFolder] : []).map((crumb, i, arr) => {
              const isLast = i === arr.length - 1;
              return (
                <React.Fragment key={crumb.id}>
                  <Icon name="chev-r" size={16} color="var(--ink-4)" />
                  <span
                    onClick={isLast ? undefined : () => onNavigate?.(spaceId ?? '', crumb.id)}
                    style={{ cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--ink)' : 'var(--ink-2)' }}
                  >
                    {crumb.name}
                  </span>
                </React.Fragment>
              );
            })}
            {folderId && !folderCrumbs.length && !currentFolder && <><Icon name="chev-r" size={16} color="var(--ink-4)" /><span>…</span></>}
          </div>

          {mode === 'browse' && spaceId && (
            <>
              <Button kind="accent" icon="upload" onClick={() => openUpload({ spaceId, folderId })}>Upload</Button>
              <Button kind="outline" icon="folder-plus" onClick={() => setShowNewFolder(true)}>New folder</Button>
            </>
          )}
        </div>

        {/* Toolbar */}
        <div className="sk-page-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Filter */}
          <Button
            kind="outline"
            size="sm"
            icon="filter"
            onClick={() => {
              setDraftTypeFilters(new Set(typeFilters));
              setDraftOwnerFilter(ownerFilter);
              setDraftTagFilter(tagFilter);
              setDraftSizeFilter(sizeFilter);
              setDraftSharedOnly(sharedOnly);
              setShowFilters(true);
            }}
          >
            Filter{activeFilterCount > 0 && (
              <span style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 16, borderRadius: 999, background: 'var(--accent-soft)', color: 'var(--accent-ink)', fontSize: 10, fontWeight: 700 }}>
                {activeFilterCount}
              </span>
            )}
          </Button>

          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', marginLeft: 4 }}>Sort</span>
          <select
            value={sort}
            onChange={e => setSort(e.target.value as SortKey)}
            className="t-input t-input--sm"
            style={{ width: 'auto', paddingRight: 28 }}
          >
            <option value="modified">Last modified</option>
            <option value="name">Name</option>
            <option value="size">Size</option>
            <option value="last_used">Last used</option>
          </select>
          <Button kind="ghost" size="sm" icon={sortAsc ? 'chev-u' : 'chev-d'} iconOnly onClick={() => setSortAsc(v => !v)} />

          <div style={{ flex: 1 }} />

          {/* View mode */}
          <div style={{ display: 'flex', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{ padding: '6px 9px', border: 'none', background: viewMode === 'grid' ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer', color: viewMode === 'grid' ? 'var(--accent)' : 'var(--ink-3)', display: 'flex', alignItems: 'center' }}
            >
              <Icon name="grid" size={14} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{ padding: '6px 9px', border: 'none', background: viewMode === 'list' ? 'var(--accent-soft)' : 'transparent', cursor: 'pointer', color: viewMode === 'list' ? 'var(--accent)' : 'var(--ink-3)', display: 'flex', alignItems: 'center' }}
            >
              <Icon name="list" size={14} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="sk-page-content" style={{ flex: 1, overflow: 'auto', padding: 18 }}>
          {isLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(192px, 1fr))', gap: 12 }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="t-skeleton" style={{ height: 180, borderRadius: 10 }} />
              ))}
            </div>
          ) : (
            <>
              {/* Folders */}
              {folders.length > 0 && (
                <>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 10 }}>
                    Folders
                  </div>
                  <div className="t-folder-grid" style={{ marginBottom: 24 }}>
                    {folders.map(folder => (
                      <FolderTile
                        key={folder.id}
                        folder={folder}
                        onClick={() => onNavigate?.(spaceId!, folder.id)}
                        onContextMenu={e => handleRightClick(e, undefined, folder)}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Files */}
              {displayItems.length === 0 && folders.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300, gap: 12, color: 'var(--ink-3)' }}>
                  <Icon name="folder" size={48} color="var(--ink-4)" />
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink-2)' }}>
                    {mode === 'trash' ? 'Trash is empty' : mode === 'starred' ? 'No starred items' : mode === 'shared' ? 'Nothing has been shared with you yet' : 'No files here'}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {mode === 'browse' && spaceId ? 'Upload files or create a folder to get started.' : ''}
                  </div>
                  {mode === 'browse' && spaceId && (
                    <Button kind="accent" icon="upload" onClick={() => openUpload({ spaceId, folderId })}>Upload files</Button>
                  )}
                </div>
              ) : viewMode === 'grid' ? (
                <div className="t-file-grid">
                  {displayItems.map(item => (
                    <FileTile
                      key={item.id}
                      item={item}
                      selected={selected.has(item.id)}
                      onClick={e => handleItemClick(item, e)}
                      onCheckboxClick={e => handleCheckboxClick(item, e)}
                      onDblClick={() => handleItemDblClick(item)}
                      onContextMenu={e => handleRightClick(e, item)}
                      onStar={() => starMutation.mutate({ id: item.id, starred: !item.starred })}
                    />
                  ))}
                </div>
              ) : (
                <FileList
                  items={displayItems}
                  selected={selected}
                  onSelect={handleSelect}
                  onRowClick={handleItemClick}
                  onDblClick={handleItemDblClick}
                  onContextMenu={(e, item) => handleRightClick(e, item)}
                  onStar={(item) => starMutation.mutate({ id: item.id, starred: !item.starred })}
                />
              )}
            </>
          )}
        </div>

        {/* Bulk bar */}
        {selArr.length >= 2 && (
          <div className="t-bulkbar">
            <span style={{ fontWeight: 600 }}>{selArr.length} selected</span>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{formatBytes(selBytes)}</span>
            {heldSel > 0 && <Chip tone="warn">{heldSel} held</Chip>}
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)' }} />
            <Button kind="ghost" size="sm" icon="download" style={{ color: 'white' }}
              onClick={() => selItems.forEach(item => downloadFile(item.id, item.name).catch((e: Error) => toast(e.message, 'danger')))}>Download</Button>
            <Button kind="ghost" size="sm" icon="star" style={{ color: allSelStarred ? '#E08C00' : 'white' }}
              onClick={() => { selItems.forEach(item => starMutation.mutate({ id: item.id, starred: !allSelStarred })); setSelected(new Set()); }}>
              {allSelStarred ? 'Unstar' : 'Star'}</Button>
            <Button kind="ghost" size="sm" icon="trash" style={{ color: 'rgba(255,100,80,0.9)' }}
              onClick={() => { selArr.forEach(id => trashMutation.mutate(id)); setSelected(new Set()); }}>
              Trash
            </Button>
            <Button kind="ghost" size="sm" icon="x" iconOnly style={{ color: 'white' }} onClick={() => setSelected(new Set())} />
          </div>
        )}
      </div>

      {/* Details panel */}
      {liveDetailItem && (
        <DetailsPanel item={liveDetailItem} onClose={() => { setDetailItem(null); setDetailTab('details'); }} onOpen={() => { openViewer(liveDetailItem); setDetailItem(null); setDetailTab('details'); }} isAdmin={isAdmin} spaceId={spaceId} initialTab={detailTab} />
      )}

      {/* Viewer */}
      {viewerItem && <Viewer item={viewerItem} onClose={() => setViewerItem(null)} />}

      {/* Filters drawer */}
      <Drawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filters"
        footer={
          <>
            <button
              onClick={() => {
                setDraftTypeFilters(new Set());
                setDraftOwnerFilter('Anyone');
                setDraftTagFilter('Any tag');
                setDraftSizeFilter('Any size');
                setDraftSharedOnly(false);
              }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', fontFamily: 'var(--font-sans)' }}
            >
              Clear all
            </button>
            <Button
              kind="accent"
              icon="check"
              onClick={() => {
                setTypeFilters(new Set(draftTypeFilters));
                setOwnerFilter(draftOwnerFilter);
                setTagFilter(draftTagFilter);
                setSizeFilter(draftSizeFilter);
                setSharedOnly(draftSharedOnly);
                setShowFilters(false);
              }}
            >
              Apply
            </Button>
          </>
        }
      >
        <div className="t-field" style={{ marginBottom: 20 }}>
          <label>Type</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {TYPE_FILTERS.map(t => {
              const active = draftTypeFilters.has(t.key);
              return (
                <button
                  key={t.key}
                  className={`t-filter-chip${active ? ' is-active' : ''}`}
                  onClick={() => {
                    setDraftTypeFilters(prev => {
                      const next = new Set(prev);
                      if (next.has(t.key)) next.delete(t.key); else next.add(t.key);
                      return next;
                    });
                  }}
                >
                  <Icon name={t.icon} size={13} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="t-field" style={{ marginBottom: 20 }}>
          <label>Owner</label>
          <select className="t-input" value={draftOwnerFilter} onChange={e => setDraftOwnerFilter(e.target.value)}>
            <option value="Anyone">Anyone</option>
            <option value="Me">Me</option>
          </select>
        </div>

        <div className="t-field" style={{ marginBottom: 20 }}>
          <label>Tag</label>
          <select className="t-input" value={draftTagFilter} onChange={e => setDraftTagFilter(e.target.value)}>
            {TAG_FILTERS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        <div className="t-field" style={{ marginBottom: 20 }}>
          <label>Size</label>
          <select className="t-input" value={draftSizeFilter} onChange={e => setDraftSizeFilter(e.target.value)}>
            {SIZE_FILTERS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <label className="t-check">
          <input type="checkbox" checked={draftSharedOnly} onChange={e => setDraftSharedOnly(e.target.checked)} />
          Shared items only
        </label>
      </Drawer>

      {/* New folder modal */}
      <Modal
        open={showNewFolder}
        onClose={() => setShowNewFolder(false)}
        title="New folder"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setShowNewFolder(false)}>Cancel</Button>
            <Button
              kind="accent"
              loading={createFolderMutation.isPending}
              onClick={() => { if (newFolderName.trim()) createFolderMutation.mutate(newFolderName.trim()); }}
            >
              Create
            </Button>
          </>
        }
      >
        <input
          autoFocus
          className="t-input"
          placeholder="Folder name"
          value={newFolderName}
          onChange={e => setNewFolderName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && newFolderName.trim()) createFolderMutation.mutate(newFolderName.trim()); }}
        />
      </Modal>

      {/* Rename modal */}
      <Modal
        open={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        title="Rename"
        size="sm"
        footer={
          <>
            <Button kind="ghost" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button
              kind="accent"
              loading={renameMutation.isPending}
              onClick={() => { if (renameTarget && renameName.trim()) renameMutation.mutate({ id: renameTarget.id, name: renameName.trim() + renameExt }); }}
            >
              Rename
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          <input
            autoFocus
            className="t-input"
            value={renameName}
            onChange={e => setRenameName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && renameTarget && renameName.trim()) renameMutation.mutate({ id: renameTarget.id, name: renameName.trim() + renameExt }); }}
            style={{ borderRadius: renameExt ? 'var(--r-2) 0 0 var(--r-2)' : undefined, flex: 1 }}
          />
          {renameExt && (
            <span style={{
              padding: '0 10px', height: 36, display: 'flex', alignItems: 'center',
              background: 'var(--paper-2)', border: '1px solid var(--line)', borderLeft: 'none',
              borderRadius: '0 var(--r-2) var(--r-2) 0', fontSize: 'var(--fs-sm)',
              color: 'var(--ink-3)', fontFamily: 'var(--font-mono)', flexShrink: 0,
            }}>
              {renameExt}
            </span>
          )}
        </div>
      </Modal>

      {/* Move to modal */}
      {showMoveModal && moveTarget && spaceId && (
        <FolderPickerModal
          open={showMoveModal}
          onClose={() => { setShowMoveModal(false); setMoveTarget(null); }}
          onConfirm={folderId => moveMutation.mutate({ id: moveTarget.id, folderId })}
          loading={moveMutation.isPending}
          spaceId={spaceId}
          currentFolderId={moveTarget.folder_id ?? null}
          title={`Move "${moveTarget.name}"`}
        />
      )}

      {/* Context menu */}
      {ctxMenu && (
        <ContextMenu
          x={ctxMenu.x}
          y={ctxMenu.y}
          items={ctxMenu.item ? buildItemContextMenu(ctxMenu.item) : [
            { label: 'Open', icon: 'folder', onClick: () => ctxMenu.folder && onNavigate?.(spaceId!, ctxMenu.folder.id) },
            { label: 'Rename', icon: 'edit' },
            { divider: true },
            { label: 'Move to Trash', icon: 'trash', danger: true,
              onClick: () => ctxMenu.folder && trashFolderMutation.mutate(ctxMenu.folder.id) },
          ]}
          onClose={() => setCtxMenu(null)}
        />
      )}
    </div>
  );
}

function FolderTile({ folder, onClick, onContextMenu }: { folder: Folder; onClick: () => void; onContextMenu: (e: React.MouseEvent) => void }) {
  const [over, setOver] = useState(false);
  return (
    <div
      className={`t-file-tile${over ? ' t-drop-target' : ''}`}
      style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={() => setOver(false)}
    >
      <div style={{ width: 36, height: 36, borderRadius: 'var(--r-2)', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name="folder" size={18} color="var(--accent)" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="t-file-name" style={{ fontWeight: 600 }}>{folder.name}</div>
        <div className="t-file-meta">{folder._count ? `${folder._count.items} item${folder._count.items === 1 ? '' : 's'}` : 'Folder'}</div>
      </div>
      <Button kind="ghost" size="xs" icon="more" iconOnly />
    </div>
  );
}

function FileTile({ item, selected, onClick, onCheckboxClick, onDblClick, onContextMenu, onStar }: {
  item: DriveItem;
  selected: boolean;
  onClick: (e: React.MouseEvent) => void;
  onCheckboxClick: (e: React.MouseEvent) => void;
  onDblClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onStar: () => void;
}) {
  const [hover, setHover] = useState(false);

  return (
    <div
      className={`t-file-tile${selected ? ' is-selected' : ''}`}
      draggable
      onClick={onClick}
      onDoubleClick={onDblClick}
      onContextMenu={onContextMenu}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className="t-file-tile-thumb">
        <Thumbnail item={item} fill />

        {/* Overlays */}
        <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4 }}>
          {(selected || hover) && (
            <div
              style={{
                width: 18, height: 18, borderRadius: '50%',
                background: selected ? 'var(--accent)' : 'rgba(255,255,255,0.9)',
                border: '1.5px solid',
                borderColor: selected ? 'var(--accent)' : 'var(--line-strong)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={onCheckboxClick}
              onDoubleClick={e => e.stopPropagation()}
            >
              {selected && <Icon name="check" size={10} color="white" />}
            </div>
          )}
          {item.hold_active && <Chip tone="warn" size="sm">Hold</Chip>}
          {item.link && <Chip tone="info" size="sm">Shared</Chip>}
        </div>

        <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
          {(hover || item.starred) && (
            <button
              onClick={e => { e.stopPropagation(); onStar(); }}
              style={{ background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: 4, padding: 3, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <Icon name="star" size={13} color={item.starred ? '#E08C00' : 'var(--ink-3)'} />
            </button>
          )}
        </div>
      </div>
      <div className="t-file-tile-foot">
        <div className="t-file-name">{item.name}</div>
        <div className="t-file-meta">
          {formatBytes(Number(item.currentVersion?.size_bytes ?? item.size_bytes ?? 0))} · {relativeTime(item.updated_at ?? item.created_at)}
        </div>
      </div>
    </div>
  );
}

const TAG_TONES: Record<string, 'accent' | 'success' | 'info' | 'warn' | 'danger'> = {
  Confidential: 'accent',
  Final: 'success',
  Draft: 'info',
  Important: 'danger',
  Review: 'warn',
};

function FileList({ items, selected, onSelect, onRowClick, onDblClick, onContextMenu, onStar, currentUserName }: {
  items: DriveItem[];
  selected: Set<string>;
  onSelect: (id: string, e: React.MouseEvent) => void;
  onRowClick: (item: DriveItem, e: React.MouseEvent) => void;
  onDblClick: (item: DriveItem) => void;
  onContextMenu: (e: React.MouseEvent, item: DriveItem) => void;
  onStar: (item: DriveItem) => void;
  currentUserName?: string;
}) {
  return (
    <div className="sk-filelist" style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-3)', overflow: 'hidden' }}>
      <table className="t-table" style={{ fontSize: 'var(--fs-md)' }}>
        <thead>
          <tr>
            <th style={{ width: 32 }}>
              <input type="checkbox" checked={items.length > 0 && items.every(i => selected.has(i.id))} onChange={() => {}} style={{ accentColor: 'var(--accent)' }} />
            </th>
            <th>Name</th>
            <th className="sk-col-owner">Owner</th>
            <th className="sk-col-used">Last used</th>
            <th>Size</th>
            <th className="sk-col-tags">Tags</th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr
              key={item.id}
              className={selected.has(item.id) ? 'is-selected' : ''}
              onClick={e => onRowClick(item, e)}
              onDoubleClick={() => onDblClick(item)}
              onContextMenu={e => onContextMenu(e, item)}
              style={{ cursor: 'pointer' }}
            >
              <td onClick={e => { e.stopPropagation(); onSelect(item.id, e); }}>
                <input type="checkbox" checked={selected.has(item.id)} onChange={() => {}} style={{ accentColor: 'var(--accent)' }} />
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Thumbnail item={item} width={30} height={30} />
                  <span style={{ fontWeight: 600 }}>{item.name}</span>
                  {item.hold_active && <Chip tone="warn" size="sm">Hold</Chip>}
                  {item.link && <Chip tone="info" size="sm">Shared</Chip>}
                </div>
              </td>
              <td className="sk-col-owner">
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <Avatar name={currentUserName ?? 'You'} style={{ width: 20, height: 20, fontSize: 9 }} />
                  <span style={{ color: 'var(--ink-2)' }}>You</span>
                </div>
              </td>
              <td className="sk-col-used" style={{ color: 'var(--ink-3)' }}>{relativeTime(item.last_accessed_at ?? item.lastUsed ?? item.updated_at ?? item.created_at)}</td>
              <td style={{ color: 'var(--ink-3)' }}>{formatBytes(Number(item.size_bytes ?? 0))}</td>
              <td className="sk-col-tags">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(item.tags ?? []).map(tag => (
                    <Chip key={tag.id} tone={TAG_TONES[tag.name] ?? 'neutral'} size="sm" dot>{tag.name}</Chip>
                  ))}
                </div>
              </td>
              <td>
                <button
                  onClick={e => { e.stopPropagation(); onContextMenu(e, item); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--ink-3)', display: 'flex' }}
                >
                  <Icon name="more" size={15} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Folder Picker ─────────────────────────────────────────────────────────

function FolderPickerNode({ folder, spaceId, selected, onSelect, depth = 0 }: {
  folder: Folder;
  spaceId: string;
  selected: string | null;
  onSelect: (id: string) => void;
  depth?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const { data: children = [] } = useQuery({
    queryKey: ['fp-folders', spaceId, folder.id],
    queryFn: () => fetchFolders(spaceId, folder.id),
    enabled: expanded,
  });
  const isSelected = selected === folder.id;

  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: `6px 10px 6px ${10 + depth * 20}px`,
          borderRadius: 6, cursor: 'pointer',
          background: isSelected ? 'var(--accent-soft)' : 'transparent',
          color: isSelected ? 'var(--accent)' : 'var(--ink)',
          userSelect: 'none',
        }}
        onClick={() => onSelect(folder.id)}
      >
        <button
          onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--ink-3)', width: 16 }}
        >
          {expanded ? <Icon name="chev-d" size={12} /> : <Icon name="chev-r" size={12} />}
        </button>
        <Icon name="folder" size={16} color={isSelected ? 'var(--accent)' : 'var(--warn)'} />
        <span style={{ fontSize: 14, fontWeight: isSelected ? 600 : 400, flex: 1 }}>{folder.name}</span>
        {isSelected && <Icon name="check" size={14} color="var(--accent)" />}
      </div>
      {expanded && children.map(child => (
        <FolderPickerNode key={child.id} folder={child} spaceId={spaceId} selected={selected} onSelect={onSelect} depth={depth + 1} />
      ))}
    </div>
  );
}

function FolderPickerModal({ open, onClose, onConfirm, loading, spaceId, currentFolderId, title }: {
  open: boolean;
  onClose: () => void;
  onConfirm: (folderId: string | null) => void;
  loading: boolean;
  spaceId: string;
  currentFolderId: string | null;
  title: string;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const { data: topFolders = [] } = useQuery({
    queryKey: ['fp-folders', spaceId, null],
    queryFn: () => fetchFolders(spaceId),
    enabled: open,
  });

  const isRootSelected = selected === '__root__';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button
            kind="accent"
            loading={loading}
            disabled={selected === null}
            onClick={() => onConfirm(isRootSelected ? null : selected)}
          >
            Move here
          </Button>
        </>
      }
    >
      <div style={{ maxHeight: 340, overflowY: 'auto', margin: '0 -16px', padding: '0 16px' }}>
        {/* Root option */}
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
            background: isRootSelected ? 'var(--accent-soft)' : 'transparent',
            color: isRootSelected ? 'var(--accent)' : 'var(--ink)',
            userSelect: 'none',
          }}
          onClick={() => setSelected('__root__')}
        >
          <div style={{ width: 16 }} />
          <Icon name="home" size={16} color={isRootSelected ? 'var(--accent)' : 'var(--ink-3)'} />
          <span style={{ fontSize: 14, fontWeight: isRootSelected ? 600 : 400, flex: 1 }}>My Drive (root)</span>
          {isRootSelected && <Icon name="check" size={14} color="var(--accent)" />}
        </div>

        {topFolders.length === 0 && (
          <div style={{ color: 'var(--ink-3)', fontSize: 13, padding: '12px 10px' }}>No folders yet</div>
        )}

        {topFolders.map(folder => (
          <FolderPickerNode
            key={folder.id}
            folder={folder}
            spaceId={spaceId}
            selected={selected}
            onSelect={setSelected}
          />
        ))}
      </div>
    </Modal>
  );
}
